import { HTMLProps, useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";

import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { createColumnHelper, RowModel } from "@tanstack/react-table";

import { IPart, IPartCategory } from "../../../models/Part";
import formatDateTime from "../../../utils/formatDateTime";
import openInNewTab from "../../../utils/openInNewTab";
import PartStatusLabel from "../../ebayOrders/PartStatusLabel";
import ClickToCopy from "../../shared/ClickToCopy";
import MarketplacePills from "../../shared/MarketplacePills";
import PagedTable from "../../shared/Tables/PagedTable";
import PartImage from "../../stock/PartImage";
import UnlistedOfferPrice from "../unlistedTable/UnlistedOfferPrice";
import { BatchItemType, IEbayListingBatchItem } from "../../../models/bulkListings/BulkListing";
import { ListingStatus } from "../../../pages/EbayListings/ViewListing";
import RecentlyListedErrorCell from "./RecentlyListedErrorCell";
import usePartDefaultCategoriesMutations from "../../../data/usePartDefaultCategoriesMutations";
import BulkListModal from "../unlistedTable/BulkListModal";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../notifications/useNotifications";
import { CheckboxInput } from "../../shared/Inputs/CheckboxInput";
import { PageMode } from "../../../models/PagedTable";
import UnlistedKTypeUpdate from "../unlistedTable/UnlistedKTypeUpdate";
import UnlistedNpmUpdate from "../unlistedTable/UnlistedMpnUpdate";
import EbayListingTitle from "../EbayListingTitle";
import UnlistedExpandedRowContent from "../unlistedTable/UnlistedExpandedRowContent";
import WarehouseLocationFromId from "../../warehouseLocations/WarehouseLocationFromId";
import TableCheckboxCol from "../../shared/Tables/IndeterminateCheckbox";
import useCategoriesPartial from "../../../data/useCategoriesPartial";
import Tabs from "../../shared/Tabs/Tabs";

export default function RecentlyListedTable({ onErrorClickRef }: { onErrorClickRef?: React.MutableRefObject<((errorMessage: string) => void) | null> }) {
  const { useBulkDefaultPartCategory } = usePartDefaultCategoriesMutations();
  const { saveHandlers: bulkSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { addNotification } = useNotification();
  const { categories: allCategories } = useCategoriesPartial();

  const [typeFilter, setTypeFilter] = useState<BatchItemType | null>(null);
  const [errorFilter, setErrorFilter] = useState<string | undefined>(undefined);
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [viewType, setViewType] = useState<number>(0);

  const viewTypes = [
    { value: 0, label: "Failed" },
    { value: 1, label: "Successful" },
    { value: 2, label: "Restricted" },
  ];

  const pageModeForViewType: Record<number, PageMode> = {
    0: PageMode.RecentlyListedFailed,
    1: PageMode.RecentlyListed,
    2: PageMode.RecentlyListedRestricted,
  };

  const [bulkListModalOpen, setBulkListModalOpen] = useState<boolean>(false);
  const [selectedPartsForBulkList, setSelectedPartsForBulkList] = useState<IPart[] | undefined>(undefined);

  const colHelper = createColumnHelper<IEbayListingBatchItem>();

  const cols = useMemo(
    () => [
      TableCheckboxCol(),
      colHelper.display({
        id: "images",
        cell: (info) => <PartImage part={info.row.original.partData} width="min-w-10 max-w-32" />
      }),
      colHelper.accessor("partData.offers", {
        header: "Listing Title",
        id: "listingTitle",
        sortDescFirst: false,
        sortingFn: "auto",
        cell: (info) => {
          const listingTitle = info.getValue()?.at(0)?.title;

          if (!listingTitle) {
            return <span className="text-red-500">N/A</span>;
          }
          return (
            <div className="flex flex-col">
              <span>{listingTitle} <span className="font-semibold">{listingTitle?.length > 0 ? `(${listingTitle.length}/80)` : ""}</span></span>
              <MarketplacePills part={info.row.original.partData} />
            </div>
          )

        },
      }),
      colHelper.accessor("partData.categoryId", {
        header: "Category",
        id: "categoryId",
        sortDescFirst: false,
        sortingFn: "auto",
        cell: (info) => {
          let part = info.row.original.partData;
          if (part.category !== null && part.category.name) {
            return <span>{part.category.name}</span>;
          }
          <span>{part.categoryId ? allCategories.data?.data?.find((cat: IPartCategory) => cat.id === info.getValue())?.name : "N/A"}</span>

        },
      }),
      colHelper.accessor("partData.tagNumber", {
        header: "Tag No.",
        id: "tagNumber",
        sortingFn: "auto",
        cell: (info) => <div className="flex flex-col">
          <ClickToCopy text={info.row.original.partData.tagNumber} />
        </div>,
      }),
      colHelper.accessor("partData.vehicle.vrm", {
        header: "Vehicle",
        id: "vrm",
        sortingFn: "auto",
        cell: (info) => <div className="flex flex-col">
          <ClickToCopy text={info.row.original.partData.vehicle?.vrm} />
          <span>{info.row.original.partData.vehicle?.combinedMake ?? ""}</span>
          <span>{info.row.original.partData.vehicle?.combinedModel ?? ""}</span>
          <ClickToCopy text={info.row.original.partData.vehicle?.combinedVIN} textClassName="text-xs" />
          <span className="text-xs">{info.row.original.partData.vehicle?.odometerInMiles ? `${info.row.original.partData.vehicle.odometerInMiles.toLocaleString()} miles` : "No Mileage Found"}</span>
          <span className="text-xs">Created: {formatDateTime(info.row.original.created, DateTime.DATE_SHORT)}</span>
          <UnlistedKTypeUpdate part={info.row.original.partData} />
        </div>,
      }),
      colHelper.accessor(row => {
        const offer = row.partData.offers[0];
        return offer ? ((offer.auctionStartPrice ?? 0) + (offer.buyItNowPrice ?? 0)) : 0;
      }, {
        id: "offer price",
        header: "Price",
        sortingFn: (rowA: any, rowB: any, columnId: any) => {
          //Sort by value of both
          return (parseFloat(rowA.original.offers[0]?.auctionStartPrice ?? 0)) + (parseFloat(rowA.original.offers[0]?.buyItNowPrice ?? 0)) -
            (parseFloat(rowB.original.offers[0]?.auctionStartPrice ?? 0)) - (parseFloat(rowB.original.offers[0]?.buyItNowPrice ?? 0));
        },
        cell: (info) => <UnlistedOfferPrice part={info.row.original.partData} />
      }),
      colHelper.display({
        id: "mpn image",
        header: "MPN",
        cell: (info) => <UnlistedNpmUpdate part={info.row.original.partData} />,
      }),
      colHelper.accessor("partData.warehouseLocationId", {
        header: "Warehouse",
        sortingFn: "auto",
        cell: (info) => <WarehouseLocationFromId id={info.getValue()} lastKnownId={info.row.original.partData.lastKnownWarehouseLocationId} lastStockChecked={info.row.original.partData.lastStockChecked} />,
      }),
      colHelper.accessor("created", {
        id: "created",
        header: "Listed On",
        cell: (info) => (
          <div className="flex flex-col items-center justify-center">
            <p>{formatDateTime(info.getValue(), DateTime.TIME_SIMPLE)}</p>
            <p>{formatDateTime(info.getValue(), DateTime.DATE_SHORT)}</p>
          </div>
        ),
      }),
      colHelper.accessor("partData.partStatus", {
        header: "Part Status",
        enableSorting: false,
        cell: (info) => <PartStatusLabel part={info.row.original.partData} />,
      }),
      colHelper.accessor("partData.offers", {
        id: "offer status",
        header: "Offer Status",
        enableSorting: false,
        cell: (info) => <ListingStatus offer={info.getValue()?.at(0)} part={info.row.original.partData} />,
      }),
      colHelper.display({
        header: "Listed Status",
        enableSorting: false,
        cell: (info) => <RecentlyListedErrorCell batchItem={info.row.original} />,
      }),
    ],
    [allCategories.isLoading, allCategories.data]
  );

  const bulkList = (rows: RowModel<IEbayListingBatchItem>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected() && row.original.isListed === false);

    if (selected.length === 0) {
      addNotification({
        primaryText: "No unlisted parts selected",
        variant: "warn"
      });
      return;
    }

    setSelectedPartsForBulkList(rows.rows.map((row) => row.original.partData));
    setBulkListModalOpen(true);
  };

  const handleApplyBulkDefaults = (rows: RowModel<IEbayListingBatchItem>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected() && row.original.isListed === false);

    if (selected.length === 0) {
      addNotification({
        primaryText: "No unlisted parts selected",
        variant: "warn"
      });
      return;
    }

    const body = {
      partIds: selected.map((row) => row.original.partDataId)
    };

    useBulkDefaultPartCategory.mutate(body as any, bulkSaveHandlers);
  };

  const handleErrorClick = (errorMessage: string) => {
    //Errors in the stats breakdown are all from the Failed bucket, so make sure that tab is the
    //one showing — otherwise the filter appears to return nothing.
    setViewType(0);
    setErrorFilter(errorMessage);
    setAdditionalBody({
      errorMessage: errorMessage
    });
  };

  const handleClearErrorFilter = () => {
    setErrorFilter(undefined);
    setAdditionalBody({});
  };

  const expandedRow = (part: IPart) => {
    return <UnlistedExpandedRowContent part={part} />;
  };

  useEffect(() => {
    if (onErrorClickRef) {
      onErrorClickRef.current = handleErrorClick;
    }
  }, [onErrorClickRef]);

  return (
    <>
      {selectedPartsForBulkList && (
        <BulkListModal
          open={bulkListModalOpen}
          setOpen={setBulkListModalOpen}
          selectedParts={selectedPartsForBulkList}
        />
      )}

      {errorFilter && (
        <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
          <span className="text-sm text-yellow-800">
            Filtering by error: {errorFilter.length > 100 ? errorFilter.substring(0, 100) + "..." : errorFilter}
          </span>
          <button
            onClick={handleClearErrorFilter}
            className="text-sm text-yellow-600 hover:text-yellow-800 underline"
          >
            Clear Filter
          </button>
        </div>
      )}

      <Tabs viewType={viewType} viewTypes={viewTypes} setViewType={setViewType} />

      <PagedTable
        url="/bulklisting/items"
        pageMode={pageModeForViewType[viewType] ?? PageMode.RecentlyListed}
        columns={cols}
        queryLabel="Search part details"
        customUrlQuery=""
        onRowClick={(x: IEbayListingBatchItem) => openInNewTab(`ebay-listings/${x.partData.id}`)}
        actionButtons={
          //Restricted items can never be listed, so listing actions are pointless (and would just
          //re-trigger the same eBay rejection) — leave the tab read only.
          viewType === 2
            ? []
            : [
              { label: "Bulk List", onClick: bulkList, isLoading: false, disabledOnNoRows: true },
              { label: "Apply Defaults", onClick: handleApplyBulkDefaults, isLoading: useBulkDefaultPartCategory.isLoading, disabledOnNoRows: true },
            ]
        }
        showTotal
        expandedRow={(batchItem) => expandedRow(batchItem.partData)}
        additionalBody={additionalBody}
      />
    </>
  )
}